/**
 * 消息定义。见 docs/ARCHITECTURE.md §5.5 / §5.6。
 *
 * 命名约定（下游四个模块都按这个走）：
 *   - client → server 用**祈使动词**：`joinRoom` / `sitDown` / `playerAction`；
 *   - server → client 用**过去式或事实陈述**：`playerJoined` / `seatChanged` / `handEnded`；
 *   - 一问一答的成对消息同名（`ping`/`pong`），靠方向区分；
 *   - 私有消息（只发给单个座位所属连接）：`actionPrompt` / `actionRejected` /
 *     `holeCardsDealt` 的 `mine` 分支 / `error`。私有消息**不 bump sv**，
 *     所以客户端绝不能按 sv 丢帧（见 envelope.ts）。
 *
 * ★★ 四条协议级安全规定（§5.5）★★
 * 1. 红线 #37：`playerAction` **不带 `seat`**。座位由服务端经
 *    `connection → sessionId → accountId → seat` 三级映射解析。
 *    客户端可以伪造任何字段，唯一被信任的是连接身份。
 * 2. 红线 #38：`sitDown` **不带 `buyIn`**、`rebuy` / `requestTopUp` **不带 `amount`**。
 *    让客户端控制筹码数额等于开了无限刷分后门（`rebuy{amount:1e12}`）。
 *    数额一律由服务端算：入座取 `config.startingStack`，买分补到起始线。
 * 3. 红线 #18：`to` 恒为 **raise-to 总额**，绝不是增量；`call`/`check`/`fold` 不带 `to`。
 *    服务端对 `bet/raise` 规范化：`to >= maxRaiseTo` 自动视为全下。
 * 4. `showCards` / `muckCards` 也不带 seat（同 1），授权与时机见 §4.11。
 */
import type { AccountId, Card, Chips, HandId, Millis, RoomCode, RoomId, SeatIndex, SessionId } from '../ids.js'
import type { AnteMode, RoomConfig } from '../config/roomConfig.js'
import type { Street } from '../poker/types.js'
import type { ErrorCode } from './errors.js'
import type { ResumeRequest } from './resume.js'
import type { PhraseId } from './talk.js'
import type {
  ActionPrompt,
  AiDifficulty,
  BlindPostView,
  BotPersona,
  ChatEntry,
  EmoteId,
  HandResultView,
  HandStartSeatView,
  HandSummary,
  HoleCardsView,
  LedgerEntry,
  MadeHandInfo,
  PlayerView,
  PotView,
  PublicAccount,
  PublicAction,
  PublicPlayer,
  PublicSeat,
  RoomClosedReason,
  RoomPhase,
  RoomPhaseReason,
  SettlementReport,
  ReadyStateView,
  TopUpOutcome,
  TransferOutcome,
  TransferRecord,
} from './view.js'

// ============================================================================
// 预动作
// ============================================================================

/**
 * 预动作（轮到自己之前先选好）。
 * `{ kind: 'call'; to }` 的 `to` 是 **raise-to 总额**：只有当届时的
 * `betToMatch === to` 才执行，对手加注了就自动取消——这正是「跟这个价，
 * 涨价就不跟」的语义，用增量表达不了。
 */
export type PreAction =
  | 'fold'
  | 'checkOrFold'
  | 'check'
  | { readonly kind: 'call'; readonly to: Chips }
  | 'callAny'

// ============================================================================
// client → server
// ============================================================================

export type ClientMessage =
  // —— 连接层 ——
  | {
      readonly type: 'hello'
      readonly protocolVersion: number
      readonly accessToken: string
      readonly deviceId: string
      readonly clientVersion: string
      readonly resume?: ResumeRequest
    }
  | { readonly type: 'ping'; readonly nonce: string; readonly t0: Millis }
  | {
      readonly type: 'pong'
      readonly nonce: string
      readonly t0: Millis
      readonly t3: Millis
      readonly ackSeq: number
    }
  /** ★ 红线 #49：必须单飞 + 2s 节流，否则弱网下自我 DDoS */
  | { readonly type: 'requestSnapshot'; readonly reason: 'gap' | 'desync' | 'manual' }
  | { readonly type: 'bye' }

  // —— 房间 ——
  | { readonly type: 'createRoom'; readonly config: Partial<RoomConfig>; readonly nickname: string }
  | {
      readonly type: 'joinRoom'
      readonly code: RoomCode
      readonly nickname: string
      readonly asSpectator?: boolean
    }
  | { readonly type: 'leaveRoom' }
  /** 多设备下抢占本 (account, room) 的行动权连接 */
  | { readonly type: 'claimActor' }
  /** ★ 红线 #38：无 buyIn，筹码数额由 config.startingStack 决定 */
  | { readonly type: 'sitDown'; readonly seat: SeatIndex }
  | { readonly type: 'standUp' }
  /**
   * 换座：从自己现在的座位挪到 `seat`（空座）。筹码、买入、补买次数、暂离与时间银行都跟人走。
   * `seat` 是「想去哪个位子」，不是身份声明：自己的座位仍由连接身份解析（红线 #37）。
   * 本手发到牌的人不能换（`E_SEAT_CHANGE_MIDHAND`），打完这手再换。
   */
  | { readonly type: 'changeSeat'; readonly seat: SeatIndex }
  /**
   * 历史消息，现在与 `requestTopUp` **完全相同**：交一笔买分申请，房主批准才到账。
   * 以前它不经房主直接补 `config.rebuyAmount`，手写一帧就能自己给自己发筹码。
   * ★ 红线 #38：无 amount。
   */
  | { readonly type: 'rebuy' }
  /**
   * 买分申请（补到起始筹码线）。
   * ★ 红线 #37：不带 seat —— 座位由连接身份解析。
   * ★ 红线 #38：不带 amount —— 补多少由服务端算（`startingStack - stack`），
   *   而且是在**生效那一刻**重算，不是申请那一刻。客户端说了不算。
   */
  | { readonly type: 'requestTopUp' }
  /** 申请人自己撤回（pending 与「已批准待生效」都可撤） */
  | { readonly type: 'cancelTopUp'; readonly requestId: string }
  /**
   * 向 `toSeat` 求购 `amount` 筹码（方向见 view.ts 的「字段方向」段：
   * 发起人是买家，`toSeat` 是卖家兼批准人）。
   *
   * ★ 红线 #37：不带自己的 seat —— 买家座位由连接身份解析。
   * ★ 红线 #38：`amount` 只是**申请额**，服务端会钳到
   *   `min(申请额, 起始线 − 买家筹码, 卖家筹码)`，并在生效那一刻重算。
   */
  | { readonly type: 'requestTransfer'; readonly toSeat: SeatIndex; readonly amount: Chips }
  /** 卖家批准（★ 批准人是被求购者，不是房主） */
  | { readonly type: 'approveTransfer'; readonly requestId: string }
  | { readonly type: 'denyTransfer'; readonly requestId: string }
  /** 申请人自己撤回 */
  | { readonly type: 'cancelTransfer'; readonly requestId: string }
  /** 手牌结束后「我看完了，继续」。★ 红线 #37：座位由连接身份解析 */
  | { readonly type: 'readyForNext' }
  | { readonly type: 'setReady'; readonly ready: boolean }
  | { readonly type: 'setSitOutNextHand'; readonly sitOut: boolean }
  | { readonly type: 'setAway'; readonly away: boolean }

  // —— 房主 ——
  /** 玩法（deckVariant）是 createOnly，改配置时不能带 */
  | { readonly type: 'updateRoomConfig'; readonly patch: Partial<Omit<RoomConfig, 'deckVariant'>> }
  | { readonly type: 'startGame' }
  | { readonly type: 'pauseGame'; readonly paused: boolean }
  | {
      readonly type: 'addBot'
      readonly seat: SeatIndex
      readonly persona: BotPersona
      readonly difficulty: AiDifficulty
    }
  | { readonly type: 'removeBot'; readonly seat: SeatIndex }
  | { readonly type: 'kickPlayer'; readonly accountId: AccountId }
  | { readonly type: 'transferHost'; readonly accountId: AccountId }
  | { readonly type: 'closeRoom' }
  /** 房主批准买分。★ 手牌进行中批准不会立刻到账，排队到手牌边界（红线 #41） */
  | { readonly type: 'approveTopUp'; readonly requestId: string }
  | { readonly type: 'denyTopUp'; readonly requestId: string }
  /** 房主强制开下一手（逃生口：不等还没点「继续」的人） */
  | { readonly type: 'forceNextHand' }
  /** 房主结束牌局 → 结算并广播战报 */
  | { readonly type: 'endGame' }

  // —— 行动 ——
  /**
   * ★ 无 `seat` 字段（红线 #37）。
   * 校验链顺序不可调换（红线 #36）：`handId` 必须先于 `seq/actionNo` 幂等分支，
   * 否则上一手的重发会被当成本手的幂等重放。
   */
  | {
      readonly type: 'playerAction'
      readonly handId: HandId
      /** = 客户端看到的 ActionPrompt.actionNo，即传输序号/幂等键 */
      readonly actionNo: number
      readonly actionToken: string
      readonly action: 'fold' | 'check' | 'call' | 'bet' | 'raise'
      /** ★ raise-to 总额（红线 #18）。仅 bet/raise 携带 */
      readonly to?: Chips
      /** 客户端生成的一次性 id，用于精确回滚那一次乐观 UI */
      readonly clientActionId: string
    }
  | { readonly type: 'useTimeBank'; readonly handId: HandId; readonly actionNo: number }
  | { readonly type: 'setPreAction'; readonly handId: HandId; readonly preAction: PreAction | null }
  /** ★ 无 seat：座位由连接身份解析（红线 #37）。时机见 §4.11 */
  | { readonly type: 'showCards'; readonly handId: HandId }
  | { readonly type: 'muckCards'; readonly handId: HandId }

  // —— 社交 ——
  | { readonly type: 'chat'; readonly text: string }
  /** 固定短语：只发编号，文字由服务端查表（`PHRASE_TEXT`），走与 chat 相同的历史与开关 */
  | { readonly type: 'phrase'; readonly phrase: PhraseId }
  | { readonly type: 'emote'; readonly emote: EmoteId; readonly targetSeat?: SeatIndex }

export type ClientMessageType = ClientMessage['type']

/** 运行时清单：网关层 schema 与限流表按它分发（§5.10）。 */
export const CLIENT_MESSAGE_TYPES = [
  'hello',
  'ping',
  'pong',
  'requestSnapshot',
  'bye',
  'createRoom',
  'joinRoom',
  'leaveRoom',
  'claimActor',
  'sitDown',
  'standUp',
  'changeSeat',
  'rebuy',
  'requestTopUp',
  'cancelTopUp',
  'requestTransfer',
  'approveTransfer',
  'denyTransfer',
  'cancelTransfer',
  'readyForNext',
  'setReady',
  'setSitOutNextHand',
  'setAway',
  'updateRoomConfig',
  'startGame',
  'pauseGame',
  'addBot',
  'removeBot',
  'kickPlayer',
  'transferHost',
  'closeRoom',
  'approveTopUp',
  'denyTopUp',
  'forceNextHand',
  'endGame',
  'playerAction',
  'useTimeBank',
  'setPreAction',
  'showCards',
  'muckCards',
  'chat',
  'phrase',
  'emote',
] as const satisfies readonly ClientMessageType[]

/**
 * ★ 红线 #34：队列满时这些**变更类**消息绝不静默丢弃，
 * 必须回 `E_RATE_LIMITED{ retryable: true }` 让客户端重发。
 * 不在此列的（chat / phrase / emote / requestSnapshot / ping / pong）可以丢。
 */
export const NEVER_DROP_MESSAGE_TYPES = [
  'hello',
  'playerAction',
  'useTimeBank',
  'showCards',
  'muckCards',
  'createRoom',
  'joinRoom',
  'leaveRoom',
  'claimActor',
  'sitDown',
  'standUp',
  'changeSeat',
  'rebuy',
  'requestTopUp',
  'cancelTopUp',
  'approveTopUp',
  'denyTopUp',
  'requestTransfer',
  'approveTransfer',
  'denyTransfer',
  'cancelTransfer',
  // ★ readyForNext / forceNextHand 绝不可丢：丢一条就是整桌停在结算浮层上干等，
  //   只能靠 90s 兜底才走得下去
  'readyForNext',
  'forceNextHand',
  'endGame',
  'setReady',
  'setSitOutNextHand',
  'setAway',
  'updateRoomConfig',
  'startGame',
  'pauseGame',
  'addBot',
  'removeBot',
  'kickPlayer',
  'transferHost',
  'closeRoom',
  'setPreAction',
  'bye',
] as const satisfies readonly ClientMessageType[]

export function isDroppableOnQueueFull(type: ClientMessageType): boolean {
  return !(NEVER_DROP_MESSAGE_TYPES as readonly string[]).includes(type)
}

// ============================================================================
// server → client
// ============================================================================

export type ServerMessage =
  // —— 连接层 ——
  /** ★ 红线 #40：welcome 恒装在 `seq = 0` 的帧里 */
  | {
      readonly type: 'welcome'
      readonly sessionId: SessionId
      /** 明文只在这里出现一次，日志必须 redact（红线 #13） */
      readonly resumeToken: string
      readonly accountId: AccountId
      readonly account: PublicAccount
      readonly serverTime: Millis
      /** 进程 ULID；与上次不同即整表重置 */
      readonly serverEpoch: string
      readonly protocolVersion: number
      /** false = 新会话，客户端必须 cursorForNewSession() 归零游标 */
      readonly resumed: boolean
      /** true = 重发缓冲补不齐，客户端应等一份完整 roomSnapshot */
      readonly gap: boolean
      readonly lastProcessedCid: number
      readonly heartbeatIntervalMs: number
    }
  | {
      readonly type: 'pong'
      readonly nonce: string
      readonly t0: Millis
      readonly t1: Millis
      readonly t2: Millis
    }
  | { readonly type: 'sessionSuperseded'; readonly byDeviceLabel: string; readonly at: Millis }
  | { readonly type: 'sessionRevoked'; readonly reason: string }
  | { readonly type: 'serverShutdown'; readonly reason: string; readonly resumeAfterMs: number }
  /**
   * 私有消息。`retryable` 必须与 `isRetryableErrorCode(code)` 一致；
   * 因限速关连接时必须**先发本消息再 close(4429)**（§5.8）。
   */
  | {
      readonly type: 'error'
      readonly code: ErrorCode
      readonly message: string
      readonly retryable: boolean
      readonly retryAfterMs?: number
      readonly refCid?: number
      readonly refClientActionId?: string
      readonly details?: Readonly<Record<string, unknown>>
    }

  // —— 全量快照（type: 'roomSnapshot'）——
  | PlayerView

  // —— 房间增量 ——
  | { readonly type: 'roomConfigChanged'; readonly config: RoomConfig; readonly appliesAt: 'now' | 'nextHand' }
  | { readonly type: 'roomPhaseChanged'; readonly phase: RoomPhase; readonly reason: RoomPhaseReason }
  | {
      readonly type: 'hostChanged'
      readonly hostAccountId: AccountId
      readonly reason: 'manual' | 'left' | 'offline'
    }
  | { readonly type: 'playerJoined'; readonly player: PublicPlayer }
  | {
      readonly type: 'playerLeft'
      readonly accountId: AccountId
      readonly reason: 'left' | 'kicked' | 'abandoned' | 'roomClosed'
    }
  | { readonly type: 'seatChanged'; readonly seat: PublicSeat }
  | {
      readonly type: 'roomClosed'
      readonly reason: RoomClosedReason
      readonly finalLedger: readonly LedgerEntry[]
    }
  /** 私有：本连接是否持有该 (account, room) 的行动权 */
  | { readonly type: 'actorChanged'; readonly isActor: boolean }

  // —— 买分与结算 ——
  /** 有人申请买分。`amount` 是申请那一刻的预估额，真正到账额在生效时重算 */
  | {
      readonly type: 'topUpRequested'
      readonly requestId: string
      readonly seat: SeatIndex
      readonly amount: Chips
      readonly requestedAt: Millis
    }
  /**
   * 申请有了结果。
   *
   * ★ `approved` 会发**两次**，靠 `appliedAt` 区分，这是红线 #41 在协议上的投影：
   *   - 无 `appliedAt` = 房主已批准，但手牌进行中，筹码要等手牌边界才到账；
   *   - 有 `appliedAt` = 筹码已经加到座位上（同一 step 里必有 `seatChanged`）。
   *   手牌边界外批准时只发后者。其余三种 outcome 只发一次且无 `appliedAt`。
   */
  | {
      readonly type: 'topUpResolved'
      readonly requestId: string
      readonly seat: SeatIndex
      readonly outcome: TopUpOutcome
      readonly appliedAt?: Millis
      /** approved 且已到账时为实际加上的筹码 */
      readonly amount?: Chips
    }
  /** 牌局结束战报（房主 `endGame` 触发） */
  | { readonly type: 'gameSettled'; readonly report: SettlementReport }

  // —— 玩家间转账 ——
  /**
   * 有人发起求购。方向见 view.ts 的「字段方向」段：
   * `fromSeat` 是买家（收筹码），`toSeat` 是卖家（批准人，出筹码）。
   * `amount` 是**钳制后的申请额上限**，真正转走多少在生效时重算。
   */
  | {
      readonly type: 'transferRequested'
      readonly requestId: string
      readonly fromSeat: SeatIndex
      readonly toSeat: SeatIndex
      readonly amount: Chips
      readonly requestedAt: Millis
    }
  /**
   * 申请有了结果。
   *
   * ★ 与 `topUpResolved` 同构：`approved` 可能发**两次**，靠 `appliedAt` 区分——
   *   无 `appliedAt` = 卖家已批准，但手牌进行中，要等手牌边界才划账（红线 #41）；
   *   有 `appliedAt` = 筹码已经划走（同一 step 里必有两条 `seatChanged`）。
   *   手牌边界外批准时只发后者。其余三种 outcome 只发一次且无 `appliedAt`。
   * ★ `record` 只在真正划账时带：它是全员可见的转账流水的增量（防 chip dumping）。
   */
  | {
      readonly type: 'transferResolved'
      readonly requestId: string
      readonly fromSeat: SeatIndex
      readonly toSeat: SeatIndex
      readonly outcome: TransferOutcome
      readonly appliedAt?: Millis
      /** approved 且已划账时为实际转走的筹码 */
      readonly amount?: Chips
      /** approved 且已划账时的流水行，直接进全员可见的转账记录 */
      readonly record?: TransferRecord
    }

  // —— 开下一局的全员确认 ——
  /**
   * 等待名单变了就发一条（有人点了继续、有人断线、有人坐出）。
   * 客户端据此渲染「2/4 已确认 · 等 老王、莉莉」——只给数字，大家就只能干等。
   *
   * ★ `state: null` = 确认窗口关了，收起结算浮层。
   *   平时不需要它——`handStarted` / `handVoided` 就是关闭信号。它存在只为了一种
   *   局面：**窗口关了却没有下一手**（最后一个人确认时房主正暂停着 / 对手刚破产 /
   *   人数不够）。那时两条关闭信号一条都不会来，客户端会把结算浮层永远挂在桌面上，
   *   下面的补分、离座什么都点不到——服务端没卡死，玩家这边卡死了。
   */
  | { readonly type: 'readyStateChanged'; readonly state: ReadyStateView | null }

  // —— 牌局增量 ——
  | {
      readonly type: 'handStarted'
      readonly handId: HandId
      readonly handNo: number
      readonly buttonSeat: SeatIndex
      readonly sbSeat: SeatIndex | null
      readonly bbSeat: SeatIndex
      readonly deadButton: boolean
      readonly sb: Chips
      readonly bb: Chips
      readonly anteMode: AnteMode
      readonly ante: Chips
      /** 不含任何底牌 */
      readonly seats: readonly HandStartSeatView[]
    }
  /**
   * 同一 step 的多条盲注合并成一条。
   * `actions`：这些强制投入在动作流水里的记录（与快照的 `hand.actionLog` 前几条逐字相同）。
   * 老服务端不带，客户端缺它时照旧只改座位。
   */
  | {
      readonly type: 'blindsPosted'
      readonly handId: HandId
      readonly posts: readonly BlindPostView[]
      readonly actions?: readonly PublicAction[]
    }
  /**
   * ★ 只能由 `projectHoleCards` 产出，**每 viewer 单独序列化**（红线 #4）。
   * `HoleCardsView` 是判别联合，所以「他人底牌」在类型上就构造不出来（红线 #1）。
   */
  | {
      readonly type: 'holeCardsDealt'
      readonly handId: HandId
      readonly seats: readonly { readonly seat: SeatIndex; readonly holeCards: HoleCardsView }[]
    }
  /** 公开消息。红线 #50：客户端只在 `seat === ownedSeat` 时触发快进 */
  | {
      readonly type: 'turnChanged'
      readonly handId: HandId
      readonly actionNo: number
      readonly seat: SeatIndex
      /** 这一步开始的时刻（服务端时间），= `PublicHand.actorSince` */
      readonly promptedAt: Millis
      readonly displayDeadlineAt: Millis
      readonly hardDeadlineAt: Millis
      readonly baseTimeoutMs: number
      readonly timeBankMsRemaining: number
    }
  /** 私有：只发给当前行动座位所属账号的连接（含 shadow 连接） */
  | { readonly type: 'actionPrompt'; readonly prompt: ActionPrompt }
  | {
      readonly type: 'actionTimerExtended'
      readonly handId: HandId
      readonly actionNo: number
      readonly displayDeadlineAt: Millis
      readonly hardDeadlineAt: Millis
      readonly timeBankMsRemaining: number
      readonly reason: 'timeBank' | 'disconnectGrace'
    }
  | {
      readonly type: 'playerActed'
      readonly handId: HandId
      readonly action: PublicAction
      readonly stackAfter: Chips
    }
  /**
   * 私有：乐观 UI 的回滚通道。
   * `prompt` 非 null 时表示行动权还在本座位，客户端直接用它重建动作区。
   */
  | {
      readonly type: 'actionRejected'
      readonly handId: HandId
      readonly clientActionId: string
      readonly code: ErrorCode
      readonly appliedBy: 'you' | 'otherDevice' | 'timeout' | null
      readonly prompt: ActionPrompt | null
    }
  | {
      readonly type: 'communityCards'
      readonly handId: HandId
      readonly street: Street
      /** 本街新发出的牌（flop 3 张，turn/river 1 张） */
      readonly cards: readonly Card[]
      readonly pacingHintMs: number
    }
  | {
      readonly type: 'uncalledBetReturned'
      readonly handId: HandId
      readonly seat: SeatIndex
      /** 退还的**增量**筹码，不是 raise-to */
      readonly amount: Chips
      readonly stackAfter: Chips
    }
  | {
      readonly type: 'potsUpdated'
      readonly handId: HandId
      readonly pots: readonly PotView[]
      readonly potTotal: Chips
    }
  /**
   * ★ 发帧例外（§5.6）：必须在 `cardsRevealed` 之后、`communityCards` 之前，
   * 且后续各街的 `communityCards` **分帧**发出（每街一帧，间隔 pacingHintMs），
   * 否则客户端一帧拿到河牌，无法慢放。
   */
  | { readonly type: 'runItOut'; readonly handId: HandId; readonly remainingStreets: readonly Street[] }
  /** ★ 唯一的「他人底牌」外泄闸门（红线 #1）。只能由 revealSeat 产出 */
  | {
      readonly type: 'cardsRevealed'
      readonly handId: HandId
      readonly seat: SeatIndex
      readonly cards: readonly [Card, Card]
      readonly hand: MadeHandInfo
      readonly voluntary: boolean
    }
  /** ★ 独立消息，**不带任何 Card 字段**（红线 #3：连 category 都不给） */
  | { readonly type: 'cardsMucked'; readonly handId: HandId; readonly seat: SeatIndex }
  | {
      readonly type: 'handEnded'
      readonly handId: HandId
      readonly result: HandResultView
      readonly summary: HandSummary
    }
  /** ★ 红线 #25：voided 手不推进按钮指针，故必须回传恢复后的 buttonSeat */
  | {
      readonly type: 'handVoided'
      readonly handId: HandId
      readonly reason: 'serverRestart' | 'hostAbort' | 'internalError'
      readonly buttonSeatRestored: SeatIndex | null
    }

  // —— 社交 ——
  | { readonly type: 'chat'; readonly entry: ChatEntry }
  | {
      readonly type: 'emote'
      readonly accountId: AccountId
      readonly seat: SeatIndex | null
      readonly emote: EmoteId
      readonly targetSeat?: SeatIndex
      /**
       * 只有 `nudge` 带：它属于哪一手的哪一步。被催方据此核对是不是**当前**这一步——
       * 断线补发的旧「催」不能让人在下一次行动时莫名震动。
       */
      readonly handId?: HandId
      readonly actionNo?: number
    }

export type ServerMessageType = ServerMessage['type']

export const SERVER_MESSAGE_TYPES = [
  'welcome',
  'pong',
  'sessionSuperseded',
  'sessionRevoked',
  'serverShutdown',
  'error',
  'roomSnapshot',
  'roomConfigChanged',
  'roomPhaseChanged',
  'hostChanged',
  'playerJoined',
  'playerLeft',
  'seatChanged',
  'roomClosed',
  'actorChanged',
  'topUpRequested',
  'topUpResolved',
  'transferRequested',
  'transferResolved',
  'readyStateChanged',
  'gameSettled',
  'handStarted',
  'blindsPosted',
  'holeCardsDealt',
  'turnChanged',
  'actionPrompt',
  'actionTimerExtended',
  'playerActed',
  'actionRejected',
  'communityCards',
  'uncalledBetReturned',
  'potsUpdated',
  'runItOut',
  'cardsRevealed',
  'cardsMucked',
  'handEnded',
  'handVoided',
  'chat',
  'emote',
] as const satisfies readonly ServerMessageType[]

/**
 * 私有消息清单：这些**只发给单个连接**，且**不 bump `sv`**。
 *
 * ★ 正因为它们不 bump sv，客户端才绝不能用 sv 做丢帧判断——
 *   那会把回滚通道整条吞掉（见 envelope.ts 文件头）。
 */
export const PRIVATE_MESSAGE_TYPES = [
  'welcome',
  'pong',
  'error',
  'actionPrompt',
  'actionRejected',
  'actorChanged',
  'sessionSuperseded',
  'sessionRevoked',
] as const satisfies readonly ServerMessageType[]

export function isPrivateMessageType(type: ServerMessageType): boolean {
  return (PRIVATE_MESSAGE_TYPES as readonly string[]).includes(type)
}

/** 按 type 取出对应的消息成员，写 handler 时省去手工 Extract。 */
export type ClientMessageOf<T extends ClientMessageType> = Extract<ClientMessage, { type: T }>
export type ServerMessageOf<T extends ServerMessageType> = Extract<ServerMessage, { type: T }>
